import { BarChartRounded, FilterListOffRounded, UploadRounded } from "@mui/icons-material";
import { Button, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { DateRangePicker, LocalizationProvider, type DateRange } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { Table as TanStackTable } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useMemo, useRef, useState } from "react";
import {
  ContainerGrid,
  HeaderLayout,
  IconLink,
  tableColumnTextFilterFn,
  TableFiltersButton,
  TeamPicker,
  type TableColumnFilter,
} from "src/components";
import { Paths } from "src/constants";
import { FormSubmissionsReportRowDto, useGetFormSubmissionsReport } from "src/http";
import { downloadTableAsCsv, setTitlePrefix } from "src/utilities";
import {
  EmployeeFeedbackSummary,
  EmployeeFeedbackTable,
  FilterPicker,
  FilterPickerFilterNames,
  FormTypePicker,
} from "./components";
import { columnsConfig } from "./utilities";

const EmployeeFeedbackReportPage = () => {
  setTitlePrefix("Employee Feedback Report");

  const [selectedFormGuids, setSelectedFormGuids] = useState<string[]>([]);
  const [selectedTeamGuids, setSelectedTeamGuids] = useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<FilterPickerFilterNames[]>([
    FilterPickerFilterNames.ShowNotStarted,
    FilterPickerFilterNames.ShowNotSubmitted,
    FilterPickerFilterNames.ShowSubmitted,
  ]);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange<DateTime>>([null, null]);
  const { data, isLoading } = useGetFormSubmissionsReport();
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnFilters, setColumnFilters] = useState<TableColumnFilter[]>([]);
  const tableRef = useRef<TanStackTable<FormSubmissionsReportRowDto>>(null);
  const columns = useMemo(() => columnsConfig(selectedTeamGuids), [selectedTeamGuids]);

  const filteredData = useMemo(
    () =>
      data?.filter((x) => {
        let isMatch = true;

        if (selectedFormGuids.length > 0) {
          isMatch = isMatch && selectedFormGuids.includes(x.formGuid);
        }

        if (selectedTeamGuids.length > 0) {
          isMatch = isMatch && x.recipientUserRoles.some((y) => selectedTeamGuids.includes(y.teamGuid));
        }

        if (selectedFilters.length > 0) {
          let isMatchFilter = false;

          selectedFilters.forEach((filter) => {
            switch (filter) {
              case FilterPickerFilterNames.ShowNotStarted:
                isMatchFilter = isMatchFilter || x.createdDateTimeUtc == null;
                break;
              case FilterPickerFilterNames.ShowNotSubmitted:
                isMatchFilter = isMatchFilter || (x.createdDateTimeUtc != null && x.submittedDateTimeUtc == null);
                break;
              case FilterPickerFilterNames.ShowSubmitted:
                isMatchFilter = isMatchFilter || x.submittedDateTimeUtc != null;
                break;
            }
          });

          // We want to OR these three filters together, but AND the result with the other filters
          isMatch = isMatch && isMatchFilter;
        }

        if (selectedDateRange[0] != null && selectedDateRange[1] != null) {
          let isCreatedMatch = false;
          let isSubmittedMatch = false;

          if (x.createdDateTimeUtc) {
            const createdDateTimeUtc = DateTime.fromISO(x.createdDateTimeUtc, { zone: "utc" });
            isCreatedMatch =
              selectedDateRange[0].startOf("day") <= createdDateTimeUtc &&
              createdDateTimeUtc <= selectedDateRange[1].endOf("day");
          }

          if (x.submittedDateTimeUtc) {
            const submittedDateTimeUtc = DateTime.fromISO(x.submittedDateTimeUtc, { zone: "utc" });
            isSubmittedMatch =
              selectedDateRange[0].startOf("day") <= submittedDateTimeUtc &&
              submittedDateTimeUtc <= selectedDateRange[1].endOf("day");
          }

          isMatch = isMatch && (isCreatedMatch || isSubmittedMatch);
        }

        if (columnFilters.length > 0) {
          isMatch = isMatch && tableColumnTextFilterFn(columns, x, columnFilters);
        }

        return isMatch;
      }),
    [data, selectedFormGuids, selectedTeamGuids, selectedFilters, selectedDateRange, columnFilters, columns],
  );

  function handleExportCsvClicked() {
    setExportMenuAnchor(null);
    downloadTableAsCsv({
      filename: `Employee Feedback Report - ${DateTime.now().toISODate()}`,
      table: tableRef.current,
    });
  }

  return (
    <HeaderLayout
      title={
        <Stack>
          <IconLink href={Paths.Reports} startIcon={<BarChartRounded fontSize="small" />}>
            Reports
          </IconLink>
          <Typography variant="h3">Employee Feedback Report</Typography>
          <Typography>
            Monitor and audit company-wide feedback processes with live metrics, people-centric table views, and
            exports.
          </Typography>
        </Stack>
      }
      toolbar={
        <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
          <Stack direction="row" spacing={1}>
            <FormTypePicker
              selectedFormGuids={selectedFormGuids}
              onChange={(formGuids) => setSelectedFormGuids(formGuids)}
            />
            <TeamPicker
              selectedTeamGuids={selectedTeamGuids}
              onTeamSelected={(value) => setSelectedTeamGuids(value)}
              onClearClicked={() => setSelectedTeamGuids([])}
            />
            <FilterPicker selectedFilters={selectedFilters} onChange={(filters) => setSelectedFilters(filters)} />
            <TableFiltersButton
              table={tableRef}
              columnFilters={columnFilters}
              onColumnFiltersChanged={(columnFilters) => setColumnFilters(columnFilters)}
            />
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateRangePicker
                format="MM/dd/yyyy"
                calendars={2}
                value={selectedDateRange}
                onChange={(e) => setSelectedDateRange(e)}
                slotProps={{
                  openPickerButton: {
                    disableRipple: true,
                  },
                  textField: {
                    sx: {
                      "& .MuiPickersInputBase-root": {
                        fontSize: "0.875rem",
                        padding: "0 10px",
                      },
                      width: "245px",
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="tertiary"
              startIcon={<FilterListOffRounded />}
              onClick={() => {
                setSelectedFormGuids([]);
                setSelectedTeamGuids([]);
                setSelectedFilters([
                  FilterPickerFilterNames.ShowNotStarted,
                  FilterPickerFilterNames.ShowNotSubmitted,
                  FilterPickerFilterNames.ShowSubmitted,
                ]);
                setSelectedDateRange([null, null]);
                setColumnFilters([]);
              }}
            >
              Reset Filters
            </Button>
            <Button
              variant="tertiary"
              startIcon={<UploadRounded />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            >
              Export
            </Button>
            <Menu
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
              anchorEl={exportMenuAnchor}
            >
              <MenuItem onClick={handleExportCsvClicked}>Table as CSV</MenuItem>
            </Menu>
          </Stack>
        </Stack>
      }
    >
      <ContainerGrid container spacing={{ xs: 0.5, sm: 1 }}>
        <ContainerGrid size={12}>
          <EmployeeFeedbackSummary data={filteredData} isLoading={isLoading} selectedTeamGuids={selectedTeamGuids} />
        </ContainerGrid>
        <ContainerGrid size={12}>
          <EmployeeFeedbackTable
            ref={tableRef}
            isLoading={isLoading}
            data={filteredData}
            selectedTeamGuids={selectedTeamGuids}
          />
        </ContainerGrid>
      </ContainerGrid>
    </HeaderLayout>
  );
};

export { EmployeeFeedbackReportPage };
